/**
 * F-JP-23's move rules — the half of quarantine that touches files.
 *
 * Quarantine *copies a record out of the way*; it is not a delete and it is not
 * a rewrite. Every branch below exists because the naive version — "move the
 * file into Quarantine" — destroys data in a case the user will actually hit:
 *
 *  - **A file already at the destination with identical bytes** means this
 *    record was quarantined before and reappeared. Removing the source is safe
 *    precisely because the preserved copy is byte-identical.
 *  - **A file already at the destination with different bytes** is two
 *    generations of one broken record, and there is no way to tell which one
 *    the user wants. Both are kept: the source stays exactly where it is.
 *  - **A move that fails** leaves the item in place. It is still preserved,
 *    just not tidied, and reporting it at its real location is what keeps the
 *    recovery row's path honest.
 *
 * And the rule that outranks all of them: bytes that could not be read are
 * never moved at all. The caller decides that (see `journalRecordIsMovable`)
 * and passes it in, because the decode pipeline is the only thing that knows.
 */

import type { JournalRecordType } from "@ff/domain";

import type { JournalFileStore } from "./files";
import { journalBytesEqual } from "./files";
import { journalQuarantineRecordPath } from "./layout";

/**
 * Where the preserved record ended up, and the bytes that are there.
 *
 * Both are read from the *final* location rather than carried over from the
 * source, because F-JP-23 computes the recovery row's byte count and digest
 * from where the file actually is — a row that hashed the source while pointing
 * at the destination would fail its own export verification.
 */
export interface JournalQuarantineResult {
  readonly path: string;
  readonly bytes: Uint8Array;
}

export async function quarantineJournalRecord(
  files: JournalFileStore,
  type: JournalRecordType,
  filenameId: string,
  sourcePath: string,
  bytes: Uint8Array,
  movable: boolean,
): Promise<JournalQuarantineResult> {
  if (!movable) return { path: sourcePath, bytes };

  const destination = journalQuarantineRecordPath(type, filenameId);
  if (destination === sourcePath) {
    // Already preserved; a later scan re-derives the same reason from the same
    // bytes, so there is nothing to move and nothing to change.
    return { path: sourcePath, bytes };
  }

  let finalPath = sourcePath;
  try {
    const existing = await files.read(destination);
    if (existing.kind === "bytes") {
      if (journalBytesEqual(existing.bytes, bytes)) {
        await files.remove(sourcePath);
        finalPath = destination;
      }
      // Different bytes: the source stays. Two damaged generations of one
      // record are two things the user may want, and this code cannot choose.
    } else if (existing.kind === "missing") {
      await files.move(sourcePath, destination);
      finalPath = destination;
    }
    // A destination that will not read is a destination we will not write over.
  } catch {
    finalPath = sourcePath;
  }

  return { path: finalPath, bytes: await bytesAt(files, finalPath, bytes) };
}

/** Re-reads the final location, falling back to what we already hold. */
async function bytesAt(
  files: JournalFileStore,
  path: string,
  fallback: Uint8Array,
): Promise<Uint8Array> {
  try {
    const read = await files.read(path);
    if (read.kind === "bytes") return read.bytes;
  } catch {
    // Fall through: the bytes in hand are the ones that were just written or
    // moved, so they still describe the record.
  }
  return fallback;
}
